import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { AlertTriangle, CalendarIcon, CheckCircle2, ChevronLeft, ChevronRight, Circle, Clock, Download, GitBranch, List, Loader2, MapPin, NotepadText, Plus, RefreshCw, Timer, Truck, Upload, X, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Calendar } from '~/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Label } from '~/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/lib/utils';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

// Types
type ActivationIdItem = {
    id: string;
    activationCode: string;
    status: string;
    store: { id: number; name: string } | null;
    assignedBy: { id: number; name: string } | null;
    assignedAt: string | null;
    usedAt: string | null;
    bloodReport: {
        id: string;
        remiHealthLabOrderId: string | null;
        appointmentId: string | null;
        customerId: number | null;
    } | null;
    notes: string | null;
    createdAt: string;
};

// Status badge colors
const statusBadgeVariant = (status: string) => {
    switch (status) {
        case 'AVAILABLE':
            return 'success';
        case 'ASSIGNED':
            return 'warning';
        case 'USED':
            return 'default';
        case 'LOST':
            return 'destructive';
        case 'EXPIRED':
            return 'secondary';
        default:
            return 'default';
    }
};

const statusLabels: Record<string, string> = {
    AVAILABLE: 'Verfügbar',
    ASSIGNED: 'Zugewiesen',
    USED: 'Verwendet',
    LOST: 'Verloren',
    EXPIRED: 'Abgelaufen',
};


// Import Activation IDs Modal
const ImportActivationIdsModal = ({
    isOpen,
    onClose,
    onSuccess,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}) => {
    const [codes, setCodes] = useState('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');

    const { data: stores } = trpc.stores.getAll.useQuery();
    const importMutation = trpc.bloodTests.importActivationIds.useMutation({
        onSuccess: (result) => {
            toast.success(`${result.imported} Aktivierungs-IDs importiert`, {
                description: result.skipped > 0 ? `${result.skipped} bereits vorhanden` : undefined,
            });
            setCodes('');
            setSelectedStoreId('');
            onSuccess();
            onClose();
        },
        onError: (error) => {
            toast.error('Fehler beim Importieren', { description: error.message });
        },
    });

    const handleImport = () => {
        const codeList = codes
            .split(/[\n,;]/)
            .map(c => c.trim())
            .filter(c => c.length > 0);

        if (codeList.length === 0) {
            toast.error('Bitte geben Sie mindestens eine Aktivierungs-ID ein');
            return;
        }

        importMutation.mutate({
            activationCodes: codeList,
            storeId: selectedStoreId ? parseInt(selectedStoreId) : undefined,
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Aktivierungs-IDs importieren</DialogTitle>
                    <DialogDescription>
                        Fügen Sie Aktivierungs-IDs ein (eine pro Zeile oder durch Komma getrennt)
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label>Aktivierungs-IDs</Label>
                        <Textarea
                            placeholder="KL01abc123&#10;KL01def456&#10;KL01ghi789"
                            value={codes}
                            onChange={(e) => setCodes(e.target.value)}
                            rows={6}
                            className="font-mono text-sm max-h-[40vh] overflow-y-auto"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Standort zuweisen (optional)</Label>
                        <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Kein Standort" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="NONE">Kein Standort</SelectItem>
                                {stores?.data?.map((store) => (
                                    <SelectItem key={store.id} value={store.id.toString()}>
                                        {store.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button onClick={handleImport} disabled={importMutation.isPending}>
                        {importMutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Importieren...
                            </>
                        ) : (
                            <>
                                <Upload className="mr-2 h-4 w-4" />
                                Importieren
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Mark as Lost Modal
const MarkAsLostModal = ({
    isOpen,
    onClose,
    activationId,
    onSuccess,
}: {
    isOpen: boolean;
    onClose: () => void;
    activationId: ActivationIdItem | null;
    onSuccess: () => void;
}) => {
    const [notes, setNotes] = useState('');

    const markAsLostMutation = trpc.bloodTests.markActivationIdAsLost.useMutation({
        onSuccess: () => {
            toast.success('Aktivierungs-ID als verloren markiert');
            setNotes('');
            onSuccess();
            onClose();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    if (!activationId) return null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Als verloren markieren</DialogTitle>
                    <DialogDescription>
                        Aktivierungs-ID <span className="font-mono">{activationId.activationCode}</span> als verloren markieren?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label>Notiz (optional)</Label>
                        <Textarea
                            placeholder="Grund für den Verlust..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => markAsLostMutation.mutate({
                            activationIdId: activationId.id,
                            notes: notes || undefined,
                        })}
                        disabled={markAsLostMutation.isPending}
                    >
                        {markAsLostMutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Markieren...
                            </>
                        ) : (
                            'Als verloren markieren'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Activation IDs Tab
const ActivationIdsTab = () => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [storeFilter, setStoreFilter] = useState<string>('');
    const [page, setPage] = useState(0);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [selectedForLost, setSelectedForLost] = useState<ActivationIdItem | null>(null);

    const { data: stores } = trpc.stores.getAll.useQuery();

    const { data: activationIds, isLoading, refetch } = trpc.bloodTests.listActivationIds.useQuery({
        status: statusFilter === 'ALL' ? undefined : statusFilter as "AVAILABLE" | "ASSIGNED" | "USED" | "LOST" | "EXPIRED",
        storeId: storeFilter ? parseInt(storeFilter) : undefined,
        page,
        pageSize: 20,
    });

    const { data: availableCounts } = trpc.bloodTests.getAvailableActivationIdsCount.useQuery();

    const columns = useMemo<ColumnDef<ActivationIdItem>[]>(() => [
        {
            accessorKey: 'activationCode',
            header: 'Code',
            cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.activationCode}</span>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => (
                <Badge variant={statusBadgeVariant(row.original.status) as 'default'}>
                    {statusLabels[row.original.status]}
                </Badge>
            ),
        },
        {
            accessorKey: 'store',
            header: 'Standort',
            cell: ({ row }) => row.original.store
                ? <Badge variant="outline">{row.original.store.name}</Badge>
                : <span className="text-muted-foreground">-</span>,
        },
        {
            accessorKey: 'customerId',
            header: 'Kunde',
            cell: ({ row }) => {
                const customerId = row.original.bloodReport?.customerId;
                if (!customerId) {
                    return <span className="text-muted-foreground">-</span>;
                }
                return (
                    <span
                        className="font-mono text-xs cursor-pointer hover:underline text-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customer/${customerId}`);
                        }}
                    >
                        {String(customerId)}
                    </span>
                );
            },
        },
        {
            accessorKey: 'appointmentId',
            header: 'Termin',
            cell: ({ row }) => {
                const appointmentId = row.original.bloodReport?.appointmentId;
                if (!appointmentId) {
                    return <span className="text-muted-foreground">-</span>;
                }
                return (
                    <span
                        className="font-mono text-xs cursor-pointer hover:underline text-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/appointment/${appointmentId}`);
                        }}
                    >
                        {appointmentId.slice(0, 8)}...
                    </span>
                );
            },
        },
    ], [navigate]);

    const table = useReactTable({
        data: activationIds?.items ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: true,
        pageCount: Math.ceil((activationIds?.totalCount ?? 0) / 20),
    });

    return (
        <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {availableCounts?.map((count) => (
                    <Card key={count.storeId ?? 'unassigned'}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                {count.storeName}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-green-600">
                                {count.availableCount}
                            </div>
                            <p className="text-xs text-muted-foreground">verfügbar</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={(v) => {
                        setStatusFilter(v);
                        setPage(0);
                    }}>
                        <SelectTrigger className="w-40">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">Alle Status</SelectItem>
                            <SelectItem value="AVAILABLE">Verfügbar</SelectItem>
                            <SelectItem value="ASSIGNED">Zugewiesen</SelectItem>
                            <SelectItem value="USED">Verwendet</SelectItem>
                            <SelectItem value="LOST">Verloren</SelectItem>
                            <SelectItem value="EXPIRED">Abgelaufen</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={storeFilter} onValueChange={(v) => {
                        setStoreFilter(v);
                        setPage(0);
                    }}>
                        <SelectTrigger className="w-48">
                            <SelectValue placeholder="Standort" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="NONE">Alle Standorte</SelectItem>
                            {stores?.data?.map((store) => (
                                <SelectItem key={store.id} value={store.id.toString()}>
                                    {store.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => refetch()}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button onClick={() => setIsImportModalOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        IDs importieren
                    </Button>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                    ))}
                </div>
            ) : (
                <>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                        <TableHead>Aktionen</TableHead>
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                            <TableCell>
                                                {row.original.status === 'AVAILABLE' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-destructive hover:text-destructive"
                                                        onClick={() => setSelectedForLost(row.original)}
                                                    >
                                                        <X className="h-4 w-4 mr-1" />
                                                        Verloren
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} className="h-24 text-center">
                                            Keine Aktivierungs-IDs gefunden.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                            Zeige {page * 20 + 1} - {Math.min((page + 1) * 20, activationIds?.totalCount ?? 0)} von {activationIds?.totalCount ?? 0}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => p + 1)}
                                disabled={(page + 1) * 20 >= (activationIds?.totalCount ?? 0)}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}

            <ImportActivationIdsModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onSuccess={() => refetch()}
            />

            <MarkAsLostModal
                isOpen={!!selectedForLost}
                onClose={() => setSelectedForLost(null)}
                activationId={selectedForLost}
                onSuccess={() => refetch()}
            />
        </div>
    );
};

// Status helpers
type RemiStatus = 'pending' | 'processing' | 'completed' | 'cancelled' | 'unknown';

const remiStatusConfig: Record<RemiStatus, { label: string; variant: string }> = {
    pending: { label: 'Ausstehend', variant: 'warning' },
    processing: { label: 'In Bearbeitung', variant: 'default' },
    completed: { label: 'Abgeschlossen', variant: 'success' },
    cancelled: { label: 'Storniert', variant: 'secondary' },
    unknown: { label: 'Unbekannt', variant: 'outline' },
};

type AnivaStatus = 'pending' | 'confirmed' | 'blood_drawn' | 'shipment_picked_up' | 'cancelled';

const anivaStatusConfig: Record<AnivaStatus, { label: string; variant: string }> = {
    pending: { label: 'Ausstehend', variant: 'warning' },
    confirmed: { label: 'Bestätigt', variant: 'default' },
    blood_drawn: { label: 'Blut abgenommen', variant: 'success' },
    shipment_picked_up: { label: 'Abholung erfolgt', variant: 'success' },
    cancelled: { label: 'Storniert', variant: 'secondary' },
};

type LabOrderItem = {
    id: string;
    provider: 'remi' | 'aniva';
    remiHealthLabOrderId: string | null;
    anivaAppointmentId: string | null;
    anivaAppointmentStatus: string | null;
    anivaBarcode: string | null;
    openHealthFileId: string | null;
    remiStatus: string | null;
    resultDate: string | null;
    createdAt: string;
    customer: { id: number; name: string; email: string | null } | null;
    appointment: {
        id: string;
        status: string;
        dateFrom: string | null;
        dateFromLocal: unknown;
        dateUntil: string | null;
        store: { id: number; name: string } | null;
    } | null;
    panelName: string | null;
    panelId: string | null;
    anivaPanels: Array<{ panel_id: string; added_at: string }> | null;
    activationCode: string | null;
    store: { id: number; name: string } | null;
    remiHealthMessage: string | null;
    lastEventAt: string | null;
    lastEventType: string | null;
    hasDocument: boolean;
    documentCreatedAt: string | null;
    openHealthStatus: string | null;
    events: Array<{ eventType: string; createdAt: string }>;
};

const formatDateTime = (dateString: string | null, dateFromLocal: unknown): string => {
    if (dateFromLocal && typeof dateFromLocal === 'object' && 'localDatetime' in (dateFromLocal as Record<string, unknown>)) {
        const local = (dateFromLocal as { localDatetime: string }).localDatetime;
        const [datePart, timePart] = local.split(' ');
        if (datePart && timePart) {
            const [y, m, d] = datePart.split('-');
            return `${d}.${m}.${y} ${timePart}`;
        }
    }
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// Flow View helpers
type FlowStepKey = 'remiOrder' | 'anivaAppointment' | 'appointment' | 'remiResults' | 'anivaResults' | 'ohUpload' | 'ohReport';

type FlowStepState = {
    key: FlowStepKey;
    label: string;
    completed: boolean;
    error: boolean;
    timestamp: Date | null;
    subtitle?: string | null;
};

const appointmentFailStatuses = new Set(['CANCELLED', 'NO_SHOW', 'CALLED_OFF', 'DECLINED']);
const appointmentDoneStatuses = new Set(['COMPLETED']);

function computeFlowSteps(item: LabOrderItem): FlowStepState[] {
    const resultsAvailableEvent = item.events.find(e => e.eventType === 'RESULTS_AVAILABLE');
    const resultTimestamp = item.resultDate
        ? new Date(item.resultDate)
        : resultsAvailableEvent
            ? new Date(resultsAvailableEvent.createdAt)
            : null;

    const ohReportStatus = item.openHealthStatus;

    const apptStatus = item.appointment?.status;
    const apptCompleted = !!apptStatus && appointmentDoneStatuses.has(apptStatus);
    const apptFailed = !!apptStatus && appointmentFailStatuses.has(apptStatus);
    const apptEndTimestamp = item.appointment?.dateUntil
        ? new Date(item.appointment.dateUntil)
        : item.appointment?.dateFrom
            ? new Date(item.appointment.dateFrom)
            : null;

    if (item.provider === 'aniva') {
        const anivaStatus = item.anivaAppointmentStatus;
        const anivaBloodDrawn = anivaStatus === 'blood_drawn' || anivaStatus === 'shipment_picked_up';
        const anivaCancelled = anivaStatus === 'cancelled';
        const anivaResultsReady = !!item.resultDate;
        const docTimestamp = item.documentCreatedAt
            ? new Date(item.documentCreatedAt)
            : resultTimestamp;

        return [
            {
                key: 'anivaAppointment',
                label: 'Aniva Termin',
                completed: anivaBloodDrawn,
                error: anivaCancelled,
                timestamp: anivaBloodDrawn ? new Date(item.createdAt) : null,
                subtitle: anivaCancelled ? '(Storniert)' : null,
            },
            {
                key: 'appointment',
                label: 'Termin',
                completed: apptCompleted,
                error: apptFailed,
                timestamp: (apptCompleted || apptFailed) ? apptEndTimestamp : null,
            },
            {
                key: 'anivaResults',
                label: 'Aniva Ergebnis',
                completed: anivaResultsReady,
                error: false,
                timestamp: anivaResultsReady ? docTimestamp : null,
            },
            {
                key: 'ohUpload',
                label: 'OH Upload',
                completed: item.hasDocument,
                error: false,
                timestamp: item.hasDocument ? docTimestamp : null,
            },
            {
                key: 'ohReport',
                label: 'OH Bericht',
                completed: ohReportStatus === 'READY',
                error: ohReportStatus === 'ERROR' || ohReportStatus === 'REJECTED',
                timestamp: null,
            },
        ];
    }

    // Remi flow
    const remiResultsReady = item.remiStatus === 'completed';

    return [
        {
            key: 'remiOrder',
            label: 'Remi Erstellt',
            completed: true,
            error: false,
            timestamp: new Date(item.createdAt),
        },
        {
            key: 'appointment',
            label: 'Termin',
            completed: apptCompleted,
            error: apptFailed,
            timestamp: (apptCompleted || apptFailed) ? apptEndTimestamp : null,
        },
        {
            key: 'remiResults',
            label: 'Remi Ergebnis',
            completed: remiResultsReady,
            error: item.remiStatus === 'cancelled',
            timestamp: remiResultsReady ? resultTimestamp : null,
            subtitle: item.remiStatus === 'cancelled' ? '(Von uns storniert)' : null,
        },
        {
            key: 'ohUpload',
            label: 'OH Upload',
            completed: item.hasDocument,
            error: false,
            timestamp: item.hasDocument ? resultTimestamp : null,
        },
        {
            key: 'ohReport',
            label: 'OH Bericht',
            completed: ohReportStatus === 'READY',
            error: ohReportStatus === 'ERROR' || ohReportStatus === 'REJECTED',
            timestamp: null,
        },
    ];
}

function formatDuration(ms: number): string {
    if (ms < 0) return '-';
    const totalMinutes = Math.floor(ms / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    if (totalDays > 0) {
        const hours = totalHours % 24;
        return hours > 0 ? `${totalDays}T ${hours}Std` : `${totalDays}T`;
    }
    if (totalHours > 0) {
        const minutes = totalMinutes % 60;
        return minutes > 0 ? `${totalHours}Std ${minutes}Min` : `${totalHours}Std`;
    }
    if (totalMinutes > 0) {
        return `${totalMinutes}Min`;
    }
    return '< 1Min';
}

// Step label mapping for the detail dialog title
const stepDialogTitles: Record<FlowStepKey, string> = {
    remiOrder: 'Remi Health – Lab Order',
    anivaAppointment: 'Aniva – Termin',
    appointment: 'Termin – Details',
    remiResults: 'Remi Health – Ergebnisse',
    anivaResults: 'Aniva – Ergebnisse',
    ohUpload: 'OpenHealth – Upload',
    ohReport: 'OpenHealth – Bericht',
};

// API Detail Dialog - shows raw API response for a step
const ApiDetailDialog = ({
    isOpen,
    onClose,
    stepKey,
    bloodReportId,
    item,
}: {
    isOpen: boolean;
    onClose: () => void;
    stepKey: FlowStepKey | null;
    bloodReportId: string;
    item: LabOrderItem;
}) => {
    const isApiStep = stepKey !== null && stepKey !== 'appointment';
    const { data, isLoading, error } = trpc.bloodTests.getLabOrderApiDetails.useQuery(
        { bloodReportId },
        { enabled: isOpen && !!bloodReportId && isApiStep },
    );

    const getContentForStep = (): { title: string; data: Record<string, unknown> | null; error: string | null } => {
        if (!stepKey) return { title: '', data: null, error: null };

        if (stepKey === 'appointment') {
            const apptData: Record<string, unknown> = item.appointment
                ? {
                    id: item.appointment.id,
                    status: item.appointment.status,
                    dateFrom: item.appointment.dateFrom,
                    dateUntil: item.appointment.dateUntil,
                    store: item.appointment.store,
                }
                : { info: 'Kein Termin verknüpft' };
            return { title: stepDialogTitles.appointment, data: apptData, error: null };
        }

        if (!data) return { title: '', data: null, error: null };

        switch (stepKey) {
            case 'remiOrder':
                return {
                    title: stepDialogTitles.remiOrder,
                    data: data.remiOrder as Record<string, unknown> | null,
                    error: data.remiError,
                };
            case 'remiResults':
                return {
                    title: stepDialogTitles.remiResults,
                    data: data.remiResults as Record<string, unknown> | null,
                    error: data.remiError,
                };
            case 'anivaAppointment':
                return {
                    title: stepDialogTitles.anivaAppointment,
                    data: data.anivaAppointment as Record<string, unknown> | null,
                    error: data.anivaError,
                };
            case 'anivaResults':
                return {
                    title: stepDialogTitles.anivaResults,
                    data: data.anivaResults as Record<string, unknown> | null,
                    error: data.anivaError,
                };
            case 'ohUpload':
            case 'ohReport':
                return {
                    title: stepDialogTitles[stepKey],
                    data: data.openHealthUpload as Record<string, unknown> | null,
                    error: data.openHealthError,
                };
        }
    };

    const content = getContentForStep();

    const renderValue = (value: unknown, depth = 0): React.ReactNode => {
        if (value === null || value === undefined) {
            return <span className="text-muted-foreground italic">null</span>;
        }
        if (typeof value === 'boolean') {
            return <span className={value ? 'text-green-600' : 'text-red-600'}>{String(value)}</span>;
        }
        if (typeof value === 'number') {
            return <span className="text-blue-600">{value}</span>;
        }
        if (typeof value === 'string') {
            if (value.length > 200) {
                return <span className="text-sm break-all">{value.slice(0, 200)}...</span>;
            }
            return <span className="text-sm break-all">{value}</span>;
        }
        if (Array.isArray(value)) {
            if (value.length === 0) return <span className="text-muted-foreground italic">[]</span>;
            return (
                <div className="pl-3 border-l border-muted">
                    {value.map((item, i) => (
                        <div key={i} className="py-0.5">
                            <span className="text-muted-foreground text-xs mr-1">[{i}]</span>
                            {renderValue(item, depth + 1)}
                        </div>
                    ))}
                </div>
            );
        }
        if (typeof value === 'object') {
            return (
                <div className={cn(depth > 0 && 'pl-3 border-l border-muted')}>
                    {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
                        <div key={k} className="py-0.5 flex gap-2">
                            <span className="text-xs font-mono text-muted-foreground shrink-0">{k}:</span>
                            <div className="min-w-0">{renderValue(v, depth + 1)}</div>
                        </div>
                    ))}
                </div>
            );
        }
        return <span>{String(value)}</span>;
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>{content.title || 'API Details'}</DialogTitle>
                    <DialogDescription>
                        {stepKey === 'appointment' ? 'Termindaten aus der Datenbank' : 'Live-Daten direkt von der API'}
                    </DialogDescription>
                </DialogHeader>
                <div className="overflow-y-auto flex-1 pr-2">
                    {isLoading && isApiStep && (
                        <div className="flex items-center gap-2 py-8 justify-center">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Lade API-Daten...</span>
                        </div>
                    )}
                    {error && (
                        <div className="text-sm text-red-600 py-4">
                            Fehler beim Laden: {error.message}
                        </div>
                    )}
                    {content.error && (
                        <div className="text-sm text-red-600 py-2 px-3 bg-red-50 rounded-md mb-3">
                            API-Fehler: {content.error}
                        </div>
                    )}
                    {data && !content.data && !content.error && !isLoading && (
                        <div className="text-sm text-muted-foreground py-4 text-center">
                            Keine Daten verfügbar für diesen Schritt.
                        </div>
                    )}
                    {content.data && (
                        <div className="text-sm font-mono">
                            {renderValue(content.data)}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Schließen</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Flow Progress horizontal stepper
const FlowProgress = ({ item, onStepClick }: { item: LabOrderItem; onStepClick: (stepKey: FlowStepKey) => void }) => {
    const steps = computeFlowSteps(item);
    const now = Date.now();

    const currentStepIndex = steps.findIndex(s => !s.completed && !s.error);

    return (
        <div className="flex items-start w-full">
            {steps.map((step, i) => {
                const isCompleted = step.completed;
                const isError = step.error;
                const isCurrent = i === currentStepIndex;
                const isFuture = !isCompleted && !isError && !isCurrent;

                return (
                    <div key={i} className="contents">
                        <div className="flex flex-col items-center" style={{ minWidth: '72px' }}>
                            <button
                                type="button"
                                onClick={() => onStepClick(step.key)}
                                className={cn(
                                    'w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-primary/40',
                                    isCompleted && 'bg-green-500 border-green-500 text-white',
                                    isError && 'bg-red-100 border-red-500 text-red-600',
                                    isCurrent && 'bg-amber-50 border-amber-500 text-amber-600 animate-pulse',
                                    isFuture && 'bg-muted border-muted-foreground/30 text-muted-foreground/50',
                                )}
                                title={`${step.label} – Klicken für API-Details`}
                            >
                                {isCompleted && <CheckCircle2 className="h-4 w-4" />}
                                {isError && <XCircle className="h-4 w-4" />}
                                {isCurrent && <Clock className="h-3.5 w-3.5" />}
                                {isFuture && <Circle className="h-3.5 w-3.5" />}
                            </button>
                            <span className="text-[11px] font-medium mt-1.5 text-center leading-tight">
                                {step.label}
                            </span>
                            {step.subtitle && (
                                <span className="text-[10px] text-red-500 text-center leading-tight">
                                    {step.subtitle}
                                </span>
                            )}
                            {step.timestamp && (
                                <span className="text-[10px] text-muted-foreground">
                                    {format(step.timestamp, 'dd.MM.yy', { locale: de })}
                                </span>
                            )}
                        </div>

                        {i < steps.length - 1 && (() => {
                            const nextStep = steps[i + 1];
                            const segmentCompleted = isCompleted && (nextStep.completed || nextStep.error);
                            const isWaiting = isCompleted && !nextStep.completed && !nextStep.error;

                            let durationText: string | null = null;
                            if (segmentCompleted && step.timestamp && nextStep.timestamp) {
                                durationText = formatDuration(nextStep.timestamp.getTime() - step.timestamp.getTime());
                            } else if (isWaiting && step.timestamp) {
                                durationText = formatDuration(now - step.timestamp.getTime());
                            }

                            return (
                                <div className="flex-1 flex flex-col items-center pt-3 min-w-[40px]">
                                    <div
                                        className={cn(
                                            'w-full h-0.5',
                                            segmentCompleted && 'bg-green-500',
                                            isWaiting && 'border-t-2 border-dashed border-amber-400',
                                            !segmentCompleted && !isWaiting && 'bg-muted-foreground/20',
                                        )}
                                    />
                                    {durationText && (
                                        <span className={cn(
                                            'text-[10px] mt-1 whitespace-nowrap',
                                            isWaiting ? 'text-amber-600 font-medium' : 'text-muted-foreground',
                                        )}>
                                            {durationText}
                                        </span>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                );
            })}
        </div>
    );
};

// Flow Card for a single lab order
const LabOrderFlowCard = ({ item, onNavigate, resolvedPanelNames }: { item: LabOrderItem; onNavigate: (path: string) => void; resolvedPanelNames?: string | null }) => {
    const steps = computeFlowSteps(item);
    const allDone = steps.every(s => s.completed);
    const hasError = steps.some(s => s.error);
    const [activeStepKey, setActiveStepKey] = useState<FlowStepKey | null>(null);

    return (
        <>
            <Card className={cn(
                'transition-colors',
                hasError && 'border-red-200',
                allDone && 'border-green-200',
            )}>
                <CardContent className="p-4">
                    <div className="flex gap-4">
                        {/* Left panel ~30% */}
                        <div className="w-[30%] min-w-[180px] space-y-2 border-r pr-4">
                            {item.customer ? (
                                <div>
                                    <a
                                        href={`/customer/${item.customer.id}`}
                                        className="font-medium text-sm hover:underline"
                                    >
                                        {item.customer.name}
                                    </a>
                                    {item.customer.email && (
                                        <div className="text-xs text-muted-foreground truncate">{item.customer.email}</div>
                                    )}
                                </div>
                            ) : (
                                <span className="text-sm text-muted-foreground">Kein Kunde</span>
                            )}

                            {item.appointment && (
                                <div className="text-xs text-muted-foreground">
                                    <span className="font-medium text-foreground">Termin: </span>
                                    {formatDateTime(item.appointment.dateFrom, item.appointment.dateFromLocal) || 'Kein Datum'}
                                </div>
                            )}

                            {item.provider === 'aniva' && item.anivaBarcode && (
                                <div className="text-xs">
                                    <span className="text-muted-foreground">Barcode: </span>
                                    <span className="font-mono">{item.anivaBarcode}</span>
                                </div>
                            )}

                            {item.provider === 'remi' && item.activationCode && (
                                <div className="text-xs">
                                    <span className="text-muted-foreground">Code: </span>
                                    <span className="font-mono">{item.activationCode}</span>
                                </div>
                            )}

                            {resolvedPanelNames ? (
                                <div className="text-xs">
                                    <span className="text-muted-foreground">Panel: </span>
                                    {resolvedPanelNames}
                                </div>
                            ) : item.provider === 'aniva' ? (
                                <div className="text-xs flex items-center gap-1 text-amber-600">
                                    <AlertTriangle className="h-3 w-3" />
                                    Kein Panel
                                </div>
                            ) : item.panelName ? (
                                <div className="text-xs">
                                    <span className="text-muted-foreground">Panel: </span>
                                    {item.panelName}
                                </div>
                            ) : null}

                            <div className="flex flex-wrap gap-1 pt-1">
                                {item.store && <Badge variant="outline" className="text-[10px] h-5">{item.store.name}</Badge>}
                                {item.provider === 'aniva' ? (
                                    <Badge variant={anivaStatusConfig[(item.anivaAppointmentStatus as AnivaStatus) || 'confirmed']?.variant as 'default' ?? 'outline'} className="text-[10px] h-5">
                                        {anivaStatusConfig[(item.anivaAppointmentStatus as AnivaStatus) || 'confirmed']?.label ?? item.anivaAppointmentStatus}
                                    </Badge>
                                ) : (
                                    <Badge variant={remiStatusConfig[(item.remiStatus as RemiStatus) || 'unknown']?.variant as 'default' ?? 'outline'} className="text-[10px] h-5">
                                        {remiStatusConfig[(item.remiStatus as RemiStatus) || 'unknown']?.label ?? item.remiStatus}
                                    </Badge>
                                )}
                                <Badge variant="outline" className="text-[10px] h-5">
                                    {item.provider === 'aniva' ? 'Aniva' : 'Remi'}
                                </Badge>
                            </div>
                        </div>

                        {/* Right panel ~70% - Flow */}
                        <div className="flex-1 flex items-center min-w-0">
                            <FlowProgress item={item} onStepClick={setActiveStepKey} />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <ApiDetailDialog
                isOpen={activeStepKey !== null}
                onClose={() => setActiveStepKey(null)}
                stepKey={activeStepKey}
                bloodReportId={item.id}
                item={item}
            />
        </>
    );
};

// Flow View - renders list of flow cards
const LabOrderFlowView = ({
    items,
    isLoading,
    onNavigate,
    resolvePanelNames,
}: {
    items: LabOrderItem[];
    isLoading: boolean;
    onNavigate: (path: string) => void;
    resolvePanelNames?: (item: LabOrderItem) => string | null;
}) => {
    if (isLoading) {
        return (
            <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-28 w-full" />
                ))}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                Keine Lab Orders gefunden.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {items.map((item) => (
                <LabOrderFlowCard key={item.id} item={item} onNavigate={onNavigate} resolvedPanelNames={resolvePanelNames?.(item)} />
            ))}
        </div>
    );
};

// Lab Orders Tab
const LabOrdersTab = () => {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState<'list' | 'flow'>('flow');
    const [providerFilter, setProviderFilter] = useState<'ALL' | 'remi' | 'aniva'>('ALL');
    const [statusFilter, setStatusFilter] = useState<RemiStatus | 'ALL'>('ALL');
    const [storeFilter, setStoreFilter] = useState<string>('');
    const [panelFilter, setPanelFilter] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [isSelectingToDate, setIsSelectingToDate] = useState(false);
    const [page, setPage] = useState(0);
    const [messageDialogText, setMessageDialogText] = useState<string | null>(null);
    const pageSize = 20;

    const { data: stores } = trpc.stores.getAll.useQuery();
    const { data: panels } = trpc.bloodTests.listPanels.useQuery();
    const { data: stats } = trpc.bloodTests.getLabOrderStats.useQuery();

    const panelsByAnivaPanelId = useMemo(() => {
        const map = new Map<string, { name: string; anivaPanelId: string | null }>();
        for (const p of panels ?? []) {
            if (p.anivaPanelId) {
                map.set(p.anivaPanelId, p);
            }
        }
        return map;
    }, [panels]);

    const resolveAnivaPanelNames = (item: LabOrderItem): string | null => {
        if (item.provider === 'aniva') {
            if (item.anivaPanels && item.anivaPanels.length > 0) {
                return item.anivaPanels
                    .map(ap => panelsByAnivaPanelId.get(ap.panel_id)?.name ?? ap.panel_id.slice(0, 8) + '...')
                    .join(', ');
            }
            return null;
        }
        return item.panelName;
    };

    const { data: labOrders, isLoading } = trpc.bloodTests.listAllLabOrders.useQuery({
        provider: providerFilter === 'ALL' ? undefined : providerFilter,
        remiStatus: statusFilter === 'ALL' ? undefined : statusFilter as "pending" | "processing" | "completed" | "cancelled",
        storeId: storeFilter ? parseInt(storeFilter) : undefined,
        panelId: panelFilter || undefined,
        dateFrom: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
        dateTo: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
        page,
        pageSize,
    });

    const [isExporting, setIsExporting] = useState(false);
    const trpcUtils = trpc.useUtils();

    const handleExportCsv = async () => {
        setIsExporting(true);
        try {
            const allData = await trpcUtils.bloodTests.listAllLabOrders.fetch({
                provider: providerFilter === 'ALL' ? undefined : providerFilter,
                remiStatus: statusFilter === 'ALL' ? undefined : statusFilter as "pending" | "processing" | "completed" | "cancelled",
                storeId: storeFilter ? parseInt(storeFilter) : undefined,
                panelId: panelFilter || undefined,
                dateFrom: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
                dateTo: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
                page: 0,
                pageSize: 20,
                exportAll: true,
            });

            const remiStatusLabels: Record<string, string> = {
                pending: 'Ausstehend',
                processing: 'In Bearbeitung',
                completed: 'Abgeschlossen',
                cancelled: 'Storniert',
                unknown: 'Unbekannt',
            };

            const anivaStatusLabels: Record<string, string> = {
                pending: 'Ausstehend',
                confirmed: 'Bestätigt',
                blood_drawn: 'Blut abgenommen',
                shipment_picked_up: 'Abholung erfolgt',
                cancelled: 'Storniert',
            };

            const headers = ['Provider', 'Kunde', 'E-Mail', 'Termin', 'Ergebnis', 'Code', 'Status', 'Panel', 'Standort', 'Nachricht'];
            const rows = (allData.items as LabOrderItem[]).map((item) => {
                const statusLabel = item.provider === 'aniva'
                    ? (anivaStatusLabels[item.anivaAppointmentStatus ?? ''] ?? item.anivaAppointmentStatus ?? '')
                    : (remiStatusLabels[item.remiStatus ?? ''] ?? item.remiStatus ?? '');
                return [
                    item.provider === 'aniva' ? 'Aniva' : 'Remi',
                    item.customer?.name ?? '',
                    item.customer?.email ?? '',
                    item.appointment?.dateFrom ? new Date(item.appointment.dateFrom).toISOString() : '',
                    item.resultDate ? new Date(item.resultDate).toISOString() : '',
                    item.provider === 'aniva' ? (item.anivaBarcode ?? '') : (item.activationCode ?? ''),
                    statusLabel,
                    item.panelName ?? '',
                    item.store?.name ?? '',
                    item.remiHealthMessage ?? '',
                ];
            });

            const escapeCsvField = (field: string) => {
                if (field.includes(',') || field.includes('"') || field.includes('\n')) {
                    return `"${field.replace(/"/g, '""')}"`;
                }
                return field;
            };

            const csvContent = [
                headers.map(escapeCsvField).join(','),
                ...rows.map(row => row.map(escapeCsvField).join(',')),
            ].join('\n');

            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `lab-orders-${format(new Date(), 'yyyy-MM-dd')}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Export fehlgeschlagen');
        } finally {
            setIsExporting(false);
        }
    };

    const columns = useMemo<ColumnDef<LabOrderItem>[]>(() => [
        {
            accessorKey: 'customer',
            header: 'Kunde',
            cell: ({ row }) => {
                const customer = row.original.customer;
                if (!customer) {
                    return <span className="text-muted-foreground">-</span>;
                }
                return (
                    <div>
                        <div
                            className="cursor-pointer hover:underline font-medium text-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/${customer.id}`);
                            }}
                        >
                            {customer.name}
                        </div>
                        {customer.email && (
                            <div className="text-xs text-muted-foreground">{customer.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'appointment',
            header: 'Termin',
            cell: ({ row }) => {
                const appointment = row.original.appointment;
                if (!appointment) {
                    return <span className="text-muted-foreground">Kein Termin</span>;
                }
                return (
                    <span className="text-sm">
                        {formatDateTime(appointment.dateFrom, appointment.dateFromLocal) || 'Kein Datum'}
                    </span>
                );
            },
        },
        {
            accessorKey: 'resultDate',
            header: 'Ergebnis',
            cell: ({ row }) => {
                const displayDate = row.original.documentCreatedAt ?? row.original.resultDate;
                if (!displayDate) {
                    return <span className="text-muted-foreground">-</span>;
                }
                return (
                    <span className="text-sm">
                        {format(new Date(displayDate), 'dd.MM.yyyy HH:mm', { locale: de })}
                    </span>
                );
            },
        },
        {
            id: 'code',
            header: 'Code',
            cell: ({ row }) => {
                const code = row.original.provider === 'aniva'
                    ? row.original.anivaBarcode
                    : row.original.activationCode;
                return code
                    ? <span className="font-mono text-xs">{code}</span>
                    : <span className="text-muted-foreground">-</span>;
            },
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                if (row.original.provider === 'aniva') {
                    const status = (row.original.anivaAppointmentStatus as AnivaStatus) || 'confirmed';
                    const config = anivaStatusConfig[status] ?? anivaStatusConfig.confirmed;
                    return (
                        <div>
                            <div className="flex items-center gap-1.5">
                                <Badge variant={config.variant as 'default'}>
                                    {config.label}
                                </Badge>
                                <Badge variant="outline" className="text-[10px] h-5">Aniva</Badge>
                            </div>
                            {row.original.lastEventAt && (
                                <div className="text-xs text-muted-foreground mt-1">
                                    {format(new Date(row.original.lastEventAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                                </div>
                            )}
                        </div>
                    );
                }
                const status = (row.original.remiStatus as RemiStatus) || 'unknown';
                const config = remiStatusConfig[status] ?? remiStatusConfig.unknown;
                return (
                    <div>
                        <Badge variant={config.variant as 'default'}>
                            {config.label}
                        </Badge>
                        {row.original.lastEventAt && (
                            <div className="text-xs text-muted-foreground mt-1">
                                {format(new Date(row.original.lastEventAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'panelName',
            header: 'Panel',
            cell: ({ row }) => {
                const resolved = resolveAnivaPanelNames(row.original);
                if (resolved) return <span className="text-sm">{resolved}</span>;
                if (row.original.provider === 'aniva') {
                    return (
                        <span className="text-xs flex items-center gap-1 text-amber-600">
                            <AlertTriangle className="h-3 w-3" />
                            Kein Panel
                        </span>
                    );
                }
                return row.original.panelName
                    ? <span className="text-sm">{row.original.panelName}</span>
                    : <span className="text-muted-foreground">-</span>;
            },
        },
        {
            accessorKey: 'store',
            header: 'Standort',
            cell: ({ row }) => row.original.store
                ? <Badge variant="outline">{row.original.store.name}</Badge>
                : <span className="text-muted-foreground">-</span>,
        },
        {
            id: 'message',
            header: '',
            cell: ({ row }) => row.original.remiHealthMessage ? (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                        e.stopPropagation();
                        setMessageDialogText(row.original.remiHealthMessage);
                    }}
                >
                    <NotepadText className="h-4 w-4 text-muted-foreground" />
                </Button>
            ) : null,
        },
    ], [navigate]);

    const table = useReactTable({
        data: (labOrders?.items as LabOrderItem[] | undefined) ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: true,
        pageCount: Math.ceil((labOrders?.totalCount ?? 0) / pageSize),
    });

    return (
        <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Übersicht</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {stats ? (
                            <>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-amber-500" />
                                        <span className="text-sm">Ausstehend</span>
                                    </div>
                                    <span className="text-lg font-bold">{stats.pendingCount}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                                        <span className="text-sm">Abgeschlossen</span>
                                    </div>
                                    <span className="text-lg font-bold text-green-600">{stats.completedCount}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <XCircle className="h-4 w-4 text-red-500" />
                                        <span className="text-sm">Storniert / Fehlgeschlagen</span>
                                    </div>
                                    <span className="text-lg font-bold text-red-600">{stats.cancelledFailedCount}</span>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-3">
                                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Durchschn. Bearbeitungszeit (seit Aniva)</CardTitle>
                        <Timer className="h-4 w-4" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div className="text-2xl font-bold">
                            {stats ? (
                                stats.avgTurnaround
                                    ? `${stats.avgTurnaround.days} Tage ${stats.avgTurnaround.hours} Std`
                                    : <span className="text-muted-foreground text-base font-normal">Keine Daten</span>
                            ) : <Skeleton className="h-8 w-24 inline-block" />}
                        </div>
                        <p className="text-xs text-muted-foreground">Nur Aufträge ab 09.02.2026</p>
                        {stats?.panelTurnarounds && stats.panelTurnarounds.length > 0 && (
                            <div className="space-y-1 pt-1 border-t">
                                {stats.panelTurnarounds.map((pt) => (
                                    <div key={pt.panelId} className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground truncate mr-2">{pt.panelName}</span>
                                        <span className="font-medium whitespace-nowrap">
                                            {pt.avg.days}d {pt.avg.hours}h
                                            <span className="text-muted-foreground font-normal ml-1">({pt.sampleCount})</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-end justify-between">
                <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Provider</Label>
                    <Select value={providerFilter} onValueChange={(v) => {
                        setProviderFilter(v as 'ALL' | 'remi' | 'aniva');
                        setStatusFilter('ALL');
                        setPage(0);
                    }}>
                        <SelectTrigger className="w-40">
                            <SelectValue placeholder="Provider" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">Alle Provider</SelectItem>
                            <SelectItem value="remi">Remi Health</SelectItem>
                            <SelectItem value="aniva">Aniva</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={statusFilter} onValueChange={(v) => {
                        setStatusFilter(v as RemiStatus | 'ALL');
                        setPage(0);
                    }}>
                        <SelectTrigger className="w-44">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">Alle Status</SelectItem>
                            <SelectItem value="pending">Ausstehend</SelectItem>
                            <SelectItem value="processing">In Bearbeitung</SelectItem>
                            <SelectItem value="completed">Abgeschlossen</SelectItem>
                            <SelectItem value="cancelled">Storniert</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Standort</Label>
                    <Select value={storeFilter} onValueChange={(v) => {
                        setStoreFilter(v === 'ALL' ? '' : v);
                        setPage(0);
                    }}>
                        <SelectTrigger className="w-48">
                            <SelectValue placeholder="Alle Standorte" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">Alle Standorte</SelectItem>
                            {stores?.data?.map((store) => (
                                <SelectItem key={store.id} value={store.id.toString()}>
                                    {store.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Panel</Label>
                    <Select value={panelFilter} onValueChange={(v) => {
                        setPanelFilter(v === 'ALL' ? '' : v);
                        setPage(0);
                    }}>
                        <SelectTrigger className="w-48">
                            <SelectValue placeholder="Alle Panels" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">Alle Panels</SelectItem>
                            {panels?.map((panel) => (
                                <SelectItem key={panel.id} value={panel.id}>
                                    {panel.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Zeitraum</Label>
                    <Popover
                        open={dateRangeOpen}
                        onOpenChange={(open) => {
                            setDateRangeOpen(open);
                            if (open) {
                                setIsSelectingToDate(false);
                            }
                        }}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    'w-[220px] justify-start text-left font-normal',
                                    !dateFrom && !dateTo && 'text-muted-foreground'
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateFrom && dateTo ? (
                                    `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ${format(dateTo, 'dd.MM.yyyy', { locale: de })}`
                                ) : dateFrom ? (
                                    `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ...`
                                ) : (
                                    'Zeitraum wählen'
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <div className="p-3 border-b h-[52px] flex flex-col justify-center">
                                <p className="text-sm text-muted-foreground">
                                    {isSelectingToDate ? 'Wähle das Enddatum' : 'Wähle das Startdatum'}
                                </p>
                                <p className="text-xs text-muted-foreground h-4">
                                    {dateFrom && isSelectingToDate
                                        ? `Start: ${format(dateFrom, 'dd. MMM yyyy', { locale: de })}`
                                        : '\u00A0'
                                    }
                                </p>
                            </div>
                            <Calendar
                                mode="range"
                                locale={de}
                                selected={
                                    isSelectingToDate
                                        ? (dateFrom ? { from: dateFrom, to: dateFrom } : undefined)
                                        : undefined
                                }
                                onDayClick={(day) => {
                                    if (!isSelectingToDate) {
                                        setDateFrom(day);
                                        setDateTo(undefined);
                                        setIsSelectingToDate(true);
                                    } else {
                                        let finalFrom = dateFrom;
                                        let finalTo = day;

                                        if (dateFrom && day < dateFrom) {
                                            finalFrom = day;
                                            finalTo = dateFrom;
                                        }

                                        setDateFrom(finalFrom);
                                        setDateTo(finalTo);
                                        setIsSelectingToDate(false);
                                        setDateRangeOpen(false);
                                        setPage(0);
                                    }
                                }}
                                numberOfMonths={2}
                                defaultMonth={dateFrom || new Date()}
                            />
                            {(dateFrom || dateTo) && (
                                <div className="p-2 border-t flex justify-end">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setDateFrom(undefined);
                                            setDateTo(undefined);
                                            setIsSelectingToDate(false);
                                            setDateRangeOpen(false);
                                            setPage(0);
                                        }}
                                    >
                                        Zurücksetzen
                                    </Button>
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>
                </div>
                </div>

                <div className="flex gap-2 items-end">
                    <div className="flex border rounded-md">
                        <Button
                            variant={viewMode === 'list' ? 'default' : 'ghost'}
                            size="sm"
                            className="rounded-r-none px-3"
                            onClick={() => setViewMode('list')}
                        >
                            <List className="h-4 w-4" />
                        </Button>
                        <Button
                            variant={viewMode === 'flow' ? 'default' : 'ghost'}
                            size="sm"
                            className="rounded-l-none px-3"
                            onClick={() => setViewMode('flow')}
                        >
                            <GitBranch className="h-4 w-4" />
                        </Button>
                    </div>
                    <Button variant="outline" onClick={handleExportCsv} disabled={isExporting}>
                        {isExporting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Download className="mr-2 h-4 w-4" />
                        )}
                        CSV Export
                    </Button>
                </div>
            </div>

            {/* Content: List or Flow view */}
            {viewMode === 'list' ? (
                <>
                    {isLoading ? (
                        <div className="space-y-2">
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map((header) => (
                                                    <TableHead key={header.id}>
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableHeader>
                                    <TableBody>
                                        {table.getRowModel().rows?.length ? (
                                            table.getRowModel().rows.map((row) => (
                                                <TableRow key={row.id}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <TableCell key={cell.id}>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                                    Keine Lab Orders gefunden.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}
                </>
            ) : (
                <LabOrderFlowView
                    items={(labOrders?.items as LabOrderItem[] | undefined) ?? []}
                    isLoading={isLoading}
                    onNavigate={navigate}
                    resolvePanelNames={resolveAnivaPanelNames}
                />
            )}

            {/* Pagination (shared between views) */}
            {!isLoading && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        {(labOrders?.totalCount ?? 0) > 0
                            ? `Zeige ${page * pageSize + 1} - ${Math.min((page + 1) * pageSize, labOrders?.totalCount ?? 0)} von ${labOrders?.totalCount ?? 0}`
                            : 'Keine Ergebnisse'
                        }
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => p + 1)}
                            disabled={(page + 1) * pageSize >= (labOrders?.totalCount ?? 0)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            <Dialog open={messageDialogText !== null} onOpenChange={(open) => !open && setMessageDialogText(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Nachricht</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm whitespace-pre-wrap">{messageDialogText}</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMessageDialogText(null)}>
                            Schließen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Panel Card (read-only summary, click to navigate to detail page)
type PanelCardProps = {
    panel: {
        id: string;
        remiPanelId: string;
        anivaPanelId: string | null;
        name: string;
        description: string;
        edtaTubeCount: number;
        serumTubeCount: number;
        fluoridTubeCount: number;
        biomarkers: string[];
        treatmentVariants: { id: string; name: string }[];
    };
};

const PanelCard = ({ panel }: PanelCardProps) => {
    const navigate = useNavigate();

    return (
        <Card
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate(`/blood-tests/panel/${panel.id}`)}
        >
            <CardHeader>
                <div className="flex items-start justify-between">
                    <div>
                        <CardTitle className="text-lg">{panel.name}</CardTitle>
                        <CardDescription className="mt-1">{panel.description}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Tube Requirements */}
                <div>
                    <h4 className="text-sm font-medium mb-2">Benötigte Röhrchen</h4>
                    <div className="flex gap-4 text-sm">
                        {panel.edtaTubeCount > 0 && (
                            <div className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                                <span>{panel.edtaTubeCount}x EDTA</span>
                            </div>
                        )}
                        {panel.serumTubeCount > 0 && (
                            <div className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-amber-800"></span>
                                <span>{panel.serumTubeCount}x Serum</span>
                            </div>
                        )}
                        {panel.fluoridTubeCount > 0 && (
                            <div className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                                <span>{panel.fluoridTubeCount}x GlucoEXACT</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Treatment Variants */}
                <div>
                    <h4 className="text-sm font-medium mb-2">Verknüpfte Behandlungen</h4>
                    <div className="flex flex-wrap gap-1">
                        {panel.treatmentVariants.length === 0 ? (
                            <span className="text-sm text-muted-foreground">Keine Behandlungen verknüpft</span>
                        ) : (
                            panel.treatmentVariants.map(tv => (
                                <Badge key={tv.id} variant="secondary" className="text-xs">
                                    {tv.name}
                                </Badge>
                            ))
                        )}
                    </div>
                </div>

                {/* Biomarkers */}
                {panel.biomarkers.length > 0 && (
                    <div>
                        <h4 className="text-sm font-medium mb-1">Biomarker ({panel.biomarkers.length})</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                            {panel.biomarkers.slice(0, 10).join(', ')}
                            {panel.biomarkers.length > 10 && ` +${panel.biomarkers.length - 10} weitere`}
                        </p>
                    </div>
                )}

                {/* Panel IDs */}
                <div className="pt-2 border-t space-y-0.5">
                    <p className="text-xs text-muted-foreground font-mono">
                        Remi ID: {panel.remiPanelId}
                    </p>
                    {panel.anivaPanelId && (
                        <p className="text-xs text-muted-foreground font-mono">
                            Aniva ID: {panel.anivaPanelId}
                        </p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

// Panels Tab
const PanelsTab = () => {
    const navigate = useNavigate();
    const { role } = useUserStore(
        useShallow((state) => ({
            role: state.user?.role,
        }))
    );
    const isAdmin = role === UserRole.ADMIN;

    const { data: panels, isLoading } = trpc.bloodTests.listPanels.useQuery();

    if (isLoading) {
        return (
            <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-48 w-full" />
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {isAdmin && (
                <div className="flex justify-end">
                    <Button onClick={() => navigate('/blood-tests/panel/new')}>
                        <Plus className="mr-2 h-4 w-4" />
                        Neues Panel
                    </Button>
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {panels?.map((panel) => (
                    <PanelCard key={panel.id} panel={panel} />
                ))}
            </div>
        </div>
    );
};

// Shipment status config
const shipmentStatusConfig: Record<string, { label: string; variant: string }> = {
    pending: { label: 'Ausstehend', variant: 'warning' },
    in_transit: { label: 'In Transit', variant: 'default' },
    delivered: { label: 'Zugestellt', variant: 'success' },
    fault: { label: 'Fehler', variant: 'destructive' },
    cancelled: { label: 'Storniert', variant: 'secondary' },
};

// Aniva Shipments Tab
const AnivaShipmentsTab = () => {
    const { data: shipments, isLoading, refetch } = trpc.bloodTests.listAnivaShipments.useQuery();
    const { data: stores } = trpc.stores.getAll.useQuery({ pageSize: 1000 });
    const { data: confirmedByLocation } = trpc.bloodTests.getTodayConfirmedAppointmentsByLocation.useQuery();
    const [storeFilter, setStoreFilter] = useState<string>('ALL');
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const cancelMutation = trpc.bloodTests.cancelAnivaShipment.useMutation({
        onSuccess: () => {
            toast.success('Sendung storniert');
            refetch();
        },
        onError: (error) => {
            toast.error('Fehler beim Stornieren', { description: error.message });
        },
    });
    const storesByLocationId = useMemo(() => {
        const map = new Map<string, string>();
        for (const store of stores?.data ?? []) {
            if (store.anivaLocationId) {
                map.set(store.anivaLocationId, store.name);
            }
        }
        return map;
    }, [stores?.data]);

    const anivaStores = useMemo(() => {
        return (stores?.data ?? []).filter(s => !!s.anivaLocationId);
    }, [stores?.data]);

    const filteredShipments = useMemo(() => {
        if (!shipments) return [];
        return shipments.filter(s => {
            if (storeFilter !== 'ALL' && s.originLocationId !== storeFilter) return false;
            if (statusFilter !== 'ALL' && s.status !== statusFilter) return false;
            return true;
        });
    }, [shipments, storeFilter, statusFilter]);

    return (
        <div className="space-y-4">
            {confirmedByLocation && confirmedByLocation.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Heutige bestätigte Termine</CardTitle>
                        <CardDescription>Standorte mit bestätigten Aniva-Terminen für heute</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-3">
                            {confirmedByLocation.map((loc) => (
                                <div key={loc.anivaLocationId} className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="flex items-center gap-3">
                                        <MapPin className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-sm font-medium">{loc.storeName}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {loc.count} bestätigte{loc.count === 1 ? 'r' : ''} Termin{loc.count === 1 ? '' : 'e'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Truck className="h-4 w-4" />
                    <span className="text-sm">Sendungen der letzten 3 Tage (nach Abholdatum)</span>
                </div>
                <Button variant="outline" size="icon" onClick={() => refetch()}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            <div className="flex items-center gap-3">
                <Select value={storeFilter} onValueChange={setStoreFilter}>
                    <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Standort" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">Alle Standorte</SelectItem>
                        {anivaStores.map((store) => (
                            <SelectItem key={store.id} value={store.anivaLocationId!}>
                                {store.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">Alle Status</SelectItem>
                        {Object.entries(shipmentStatusConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                                {config.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {isLoading ? (
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                    ))}
                </div>
            ) : !filteredShipments.length ? (
                <div className="text-center py-12 text-muted-foreground">
                    {shipments?.length ? 'Keine Sendungen für die gewählten Filter gefunden.' : 'Keine Sendungen in den letzten 3 Tagen gefunden.'}
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Standort</TableHead>
                                <TableHead>Abholung</TableHead>
                                <TableHead>Erstellt am</TableHead>
                                <TableHead>Aktualisiert am</TableHead>
                                <TableHead>Aktionen</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredShipments.map((shipment) => {
                                const config = shipmentStatusConfig[shipment.status] ?? { label: shipment.status, variant: 'outline' };
                                const storeName = shipment.originLocationId ? storesByLocationId.get(shipment.originLocationId) : null;
                                return (
                                    <TableRow key={shipment.id}>
                                        <TableCell>
                                            <span className="font-mono text-xs">{shipment.id}</span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={config.variant as 'default'}>
                                                {config.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                {storeName ?? shipment.originCity ?? <span className="text-muted-foreground">-</span>}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {shipment.pickupFrom && shipment.pickupTill
                                                ? `${format(new Date(shipment.pickupFrom), 'dd.MM. HH:mm', { locale: de })} – ${format(new Date(shipment.pickupTill), 'HH:mm', { locale: de })}`
                                                : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {shipment.createdAt
                                                ? format(new Date(shipment.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })
                                                : '-'}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {shipment.updatedAt
                                                ? format(new Date(shipment.updatedAt), 'dd.MM.yyyy HH:mm', { locale: de })
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {shipment.status === 'pending' ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-destructive hover:text-destructive"
                                                    disabled={cancelMutation.isPending}
                                                    onClick={() => cancelMutation.mutate({ shipmentId: shipment.id })}
                                                >
                                                    {cancelMutation.isPending ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <X className="h-4 w-4 mr-1" />
                                                            Stornieren
                                                        </>
                                                    )}
                                                </Button>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
};

// Main Page Component
const BloodTestManagement = () => {
    return (
        <Content>
            <div className="p-6 space-y-6">
                <div>
                    <h1 className="text-2xl font-bold">Bluttest-Verwaltung</h1>
                    <p className="text-muted-foreground">
                        Verwalten Sie Aktivierungs-IDs und Bluttest-Panels
                    </p>
                </div>

                <Tabs defaultValue="lab-orders">
                    <TabsList>
                        <TabsTrigger value="lab-orders">Lab Orders</TabsTrigger>
                        <TabsTrigger value="activation-ids">Aktivierungs-IDs</TabsTrigger>
                        <TabsTrigger value="panels">Panels</TabsTrigger>
                        <TabsTrigger value="aniva-shipments">Aniva Shipments</TabsTrigger>
                    </TabsList>

                    <TabsContent value="lab-orders" className="mt-4">
                        <LabOrdersTab />
                    </TabsContent>

                    <TabsContent value="activation-ids" className="mt-4">
                        <ActivationIdsTab />
                    </TabsContent>

                    <TabsContent value="panels" className="mt-4">
                        <PanelsTab />
                    </TabsContent>

                    <TabsContent value="aniva-shipments" className="mt-4">
                        <AnivaShipmentsTab />
                    </TabsContent>
                </Tabs>
            </div>
        </Content>
    );
};

export default BloodTestManagement;
